import './productCategorySelector.scss';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Skeleton } from '@eliberty/design-system-react';

import type { DisplayMode } from '~atoms';
import {
  DISPLAY_MODE,
  ErrorMessage,
  SelectorHeader,
  SelectorLayout,
  StatItem,
  TextHTML,
} from '~atoms';
import { useSelectorCollapse } from '~hooks';
import { CategoryDescriptionModal } from '~molecules';
import type { ProductCategoryOption } from '~types/productCategory';

const CATEGORY_SKELETON_KEYS = ['category-skeleton-0', 'category-skeleton-1'];

const CategoryDetails = ({ category }: { category: ProductCategoryOption }) => (
  <>
    <span className='ProductCategorySelector_listItemName'>{category.label}</span>
    {category.shortDescription && (
      <div className='ProductCategorySelector_listItemDesc'>
        <TextHTML text={category.shortDescription} />
      </div>
    )}
    {(category.numTotalSlopes != null || category.numSkiLift != null) && (
      <div className='ProductCategorySelector_listItemBadges'>
        {category.numTotalSlopes != null && (
          <StatItem
            icon='mountain-skiFlag'
            count={category.numTotalSlopes}
            labelId='slopes'
            defaultMessage='slopes'
          />
        )}
        {category.numSkiLift != null && (
          <StatItem
            icon='mountain-lift'
            count={category.numSkiLift}
            labelId='skiLifts'
            defaultMessage='ski lifts'
          />
        )}
      </div>
    )}
  </>
);

interface ProductCategorySelectorProps {
  productCategories: ProductCategoryOption[];
  selectedProductCategory?: string;
  onProductCategoryChange: (shortname: string) => void;
  isLoading?: boolean;
  hasError?: boolean;
  displayMode?: DisplayMode;
  forceCollapsed?: boolean;
  onPopoverToggle?: () => void;
  onClickOutside?: () => void;
}

const ProductCategorySelector = ({
  productCategories,
  selectedProductCategory,
  onProductCategoryChange,
  isLoading = false,
  hasError = false,
  displayMode = DISPLAY_MODE.COLLAPSE,
  forceCollapsed,
  onPopoverToggle,
  onClickOutside,
}: ProductCategorySelectorProps) => {
  const intl = useIntl();
  const [internalSelected, setInternalSelected] = useState<string | undefined>(
    selectedProductCategory,
  );

  const { isCollapsed, setIsCollapsed, toggle } = useSelectorCollapse({
    displayMode,
    forceCollapsed,
    initialCollapsed: displayMode === DISPLAY_MODE.POPOVER,
    onPopoverToggle,
  });

  const selectedCategoryLabel = useMemo(
    () => productCategories.find((c) => c.shortname === internalSelected)?.label,
    [productCategories, internalSelected],
  );

  const handleProductCategoryChange = useCallback(
    (shortname: string) => {
      setInternalSelected(shortname);
      onProductCategoryChange(shortname);
      setIsCollapsed(true);
    },
    [onProductCategoryChange, setIsCollapsed],
  );

  useEffect(() => {
    setInternalSelected(selectedProductCategory);
    if (displayMode === DISPLAY_MODE.COLLAPSE) {
      setIsCollapsed(!!selectedProductCategory);
    }
  }, [selectedProductCategory, displayMode, setIsCollapsed]);

  const showSkeleton = isLoading && productCategories.length === 0;

  const selectorHeader = (
    <SelectorHeader
      isClosed={isCollapsed}
      isClickable={
        displayMode === DISPLAY_MODE.COLLAPSE && !!(selectedProductCategory && isCollapsed)
      }
      onToggle={() => setIsCollapsed(!isCollapsed)}
      onHeaderClick={() => setIsCollapsed(false)}
      showToggleButton={displayMode === DISPLAY_MODE.COLLAPSE && !!selectedProductCategory}
    >
      <div className='ProductCategorySelector_title'>
        <div className='ProductCategorySelector_domain'>
          <FormattedMessage id='domain' defaultMessage='Domain' />
        </div>
        {selectedCategoryLabel && (displayMode === DISPLAY_MODE.POPOVER || isCollapsed) && (
          <div className='ProductCategorySelector_value'>{selectedCategoryLabel}</div>
        )}
      </div>
    </SelectorHeader>
  );

  return (
    <div className='ProductCategorySelector'>
      <SelectorLayout
        displayMode={displayMode}
        isOpen={!isCollapsed}
        onToggle={toggle}
        onClickOutside={onClickOutside}
        header={selectorHeader}
        popoverWidth='fit-content'
      >
        <div className='ProductCategorySelector_content'>
          {showSkeleton ? (
            <ul className='ProductCategorySelector_list'>
              {CATEGORY_SKELETON_KEYS.map((key) => (
                <li key={key}>
                  <div className='ProductCategorySelector_listItem ProductCategorySelector_listItem--skeleton'>
                    <Skeleton data-testid='Skeleton' height='22px' width='40%' radius='4px' />
                    <Skeleton data-testid='Skeleton' height='44px' width='100%' radius='4px' />
                    <div className='ProductCategorySelector_listItemBadges'>
                      <Skeleton data-testid='Skeleton' height='28px' width='100px' radius='40px' />
                      <Skeleton data-testid='Skeleton' height='28px' width='130px' radius='40px' />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <ul className='ProductCategorySelector_list'>
              {productCategories.map((category) => {
                const isSelected = internalSelected === category.shortname;

                return (
                  <li
                    key={category.shortname}
                    className={`ProductCategorySelector_listItem${isSelected ? ' ProductCategorySelector_listItem--selected' : ''}`}
                  >
                    <button
                      type='button'
                      className='ProductCategorySelector_listItemButton'
                      onClick={() => handleProductCategoryChange(category.shortname)}
                      aria-label={intl.formatMessage(
                        {
                          defaultMessage: `Choose the domain {categoryName}`,
                          id: 'chooseDomain',
                        },
                        { categoryName: category.label },
                      )}
                    />
                    <CategoryDetails category={category} />
                    {category.additionalInfo?.description && (
                      <CategoryDescriptionModal
                        additionalInfo={category.additionalInfo}
                        categoryName={category.label}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {hasError && !isLoading && productCategories.length === 0 && (
            <ErrorMessage
              messageId='noDomainAvailable'
              defaultMessage='No domain available at the moment.'
            />
          )}
        </div>
      </SelectorLayout>
    </div>
  );
};

export default ProductCategorySelector;
